/*
 * L'ÂME ANIMÉE DU SITE — 22 octobre 2026, demandé par Josaphat :
 * « la plateforme Koinonia ultra animée ».
 *
 * TOUT vit ici, à côté : aucun composant React n'est modifié. Le routeur
 * appelle deux fonctions (`demarrerAnimations` une fois, `animerEcran` à
 * chaque page), le reste s'accroche tout seul — un observateur voit naître
 * les cartes, une écoute de clics fait éclater les réactions. Retirer ce
 * module et ses deux appels rend le site exactement comme avant.
 *
 * ⚠️ TROIS GARDES, parce que les téléphones des membres sont modestes et que
 * le mouvement est un plaisir, pas un droit :
 *   · « Réduire les animations » du téléphone est respecté à la lettre ;
 *   · un appareil annonçant 2 Go de mémoire ou moins n'anime rien ;
 *   · le défilement reste NATIF partout (voir la note sur Lenis plus bas).
 * Quand une garde dit non, ce module ne pose ni classe, ni écoute, ni
 * minuterie : coût zéro.
 */
import gsap from "gsap";
/* ⚠️ LENIS A ÉTÉ RETIRÉ LE 24 OCTOBRE 2026, un jour après sa pose. Le
   « défilement doux » détourne la molette pour faire glisser LA PAGE — or
   l'application défile dans un cadre intérieur, pas la page. Sur MacBook,
   la molette ne faisait donc plus rien du tout : vécu par Josaphat. Le
   défilement natif était déjà le bon ; on ne « l'améliore » plus. */

const REDUIT = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const MODESTE = (navigator.deviceMemory || 4) <= 2;
const ANIMER = !REDUIT && !MODESTE;

/* Ce qui NAÎT dans la page et mérite une entrée en scène. Les retards se
   comptent par image d'écran : dix cartes arrivées ensemble se suivent en
   cascade, puis le compteur repart à zéro. */
const NAISSANTS = ".post, .story, .commentaire, .notification, .carte-espace";
let retard = 0;
let remiseAZero = false;

function accueillir(noeud) {
  if (noeud.dataset.anime) return;
  noeud.dataset.anime = "1";
  noeud.style.animationDelay = `${Math.min(retard++ * 45, 420)}ms`;
  noeud.classList.add("est-ne");
  if (!remiseAZero) {
    remiseAZero = true;
    requestAnimationFrame(() => { retard = 0; remiseAZero = false; });
  }
}

/* L'éclat d'une réaction : une poignée d'étincelles qui jaillissent du bouton.
   L'emoji du bouton lui-même si on le trouve, des étoiles sinon. */
function eclater(x, y, emoji) {
  const brins = "✦✧".split("");
  for (let i = 0; i < 9; i++) {
    const b = document.createElement("span");
    b.className = "etincelle";
    b.textContent = i < 3 && emoji ? emoji : brins[i % brins.length];
    b.style.left = `${x}px`;
    b.style.top = `${y}px`;
    document.body.appendChild(b);
    const angle = (Math.PI * 2 * i) / 9 + Math.random() * 0.6;
    gsap.fromTo(
      b,
      { scale: 0.4, opacity: 1 },
      {
        x: Math.cos(angle) * (36 + Math.random() * 44),
        y: Math.sin(angle) * (36 + Math.random() * 44) - 24,
        scale: 1 + Math.random() * 0.6,
        opacity: 0,
        duration: 0.7 + Math.random() * 0.3,
        ease: "power2.out",
        onComplete: () => b.remove(),
      }
    );
  }
}

/** À l'ouverture, une seule fois. Sans effet si les gardes disent non. */
export function demarrerAnimations() {
  if (!ANIMER) return;
  document.documentElement.classList.add("est-anime");

  // Les naissances : tout ce qui apparaît entre en scène, où que ce soit.
  new MutationObserver((lots) => {
    for (const lot of lots)
      for (const n of lot.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches?.(NAISSANTS)) accueillir(n);
        else n.querySelectorAll?.(NAISSANTS).forEach(accueillir);
      }
  }).observe(document.body, { childList: true, subtree: true });

  // Les réactions éclatent ; les onglets rebondissent. Une seule écoute,
  // déléguée : les boutons, eux, naissent et meurent avec React.
  document.addEventListener("click", (e) => {
    const bouton = e.target.closest?.(
      ".post__action-enveloppe > .post__action, .reaction-choix, .apercu-image__action:first-child"
    );
    if (bouton) {
      const r = bouton.getBoundingClientRect();
      const emoji = bouton.textContent.match(/\p{Extended_Pictographic}/u)?.[0];
      eclater(r.left + r.width / 2, r.top + r.height / 2, emoji);
      gsap.fromTo(bouton, { scale: 0.9 }, { scale: 1, duration: 0.4, ease: "back.out(3)" });
      return;
    }
    const onglet = e.target.closest?.("[data-onglet-nav]");
    if (onglet)
      gsap.fromTo(onglet, { scale: 0.82 }, { scale: 1, duration: 0.45, ease: "back.out(2.5)" });
  });
}

/** À chaque changement de page : l'écran glisse en place. Les cartes, elles,
    sont accueillies une à une par l'observateur ci-dessus. */
export function animerEcran(vue) {
  if (!ANIMER) return;
  gsap.fromTo(
    vue,
    { opacity: 0, y: 14 },
    { opacity: 1, y: 0, duration: 0.3, ease: "power2.out", clearProps: "all" }
  );
}
